import React, { useEffect, type ReactNode } from 'react';
import { styled } from '@compiled/react';
import { colors } from '@atlaskit/theme';
// eslint-disable-next-line import/order
import { token } from '@atlaskit/tokens';

import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import { usePreviousWithInitial } from '@atlassian/jira-platform-react-hooks-use-previous/src/common/utils/index.tsx';
import { timelineScroller } from '@atlassian/jira-software-deployments-common-table/src/utils/timeline-scroller.tsx';
import { useSidebarStore } from '../../controllers/sidebar/main.tsx';
import { SidebarStatuses } from '../../controllers/sidebar/types.tsx';
import { getScrollAmountForSidebarClosing } from './utils';

const sidebarWidth = gridSize * 50; // 400px

type Props = {
	children: ReactNode;
};

const Sidebar = ({ children }: Props) => {
	const [sidebarState] = useSidebarStore();
	const isOpen = sidebarState.status !== SidebarStatuses.CLOSED;
	const wasOpen = usePreviousWithInitial(isOpen);
	const scrollState = __SERVER__ ? null : timelineScroller.getScrollState();

	useEffect(() => {
		if (wasOpen && !isOpen && scrollState) {
			const scrollAmount = getScrollAmountForSidebarClosing(scrollState, sidebarWidth);
			timelineScroller.scrollByPixels({ left: scrollAmount });
		} else if (isOpen && !wasOpen) {
			timelineScroller.scrollByPixels({ left: sidebarWidth });
		}
	}, [isOpen, wasOpen, scrollState]);

	return isOpen ? (
		<SidebarContainer
			data-testid="software-deployments.ui.sidebar.sidebar-container"
			width={sidebarWidth}
		>
			{children}
		</SidebarContainer>
	) : null;
};

export { Sidebar };
export default Sidebar;

type ContainerProps = {
	width: number;
	children: ReactNode;
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SidebarContainer = styled.div<ContainerProps>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	width: ({ width }) => `${width}px`,
	position: 'relative',
	paddingLeft: token('space.300', '24px'),
	boxSizing: 'border-box',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	backgroundColor: token('color.background.neutral.subtle', colors.N0),
	overflow: 'hidden',
});
